import React, { useState } from 'react';
import { Modal, Button, DatePicker, Select, Form } from 'antd';
import dayjs from 'dayjs';
import { NewSearchableDropdown } from '../../../../../shared';
import {
  FilterOperatorsEnum,
  IBrandEntity,
  IBrandModelEntity,
  IModelColorsEntity,
  IProductTypeEntity,
  IVendorEntity, ProcurementTypeEnum,
} from '@rasayi-workspace/shared';

const { Option } = Select;


interface Filters {
  procurementType: string;
  vendor: string[];
  productType: string;
  brand: string;
  model: string;
  color: string;
  date: string;
}

interface ViewReportProps {
  visible: boolean;
  onClose: () => void;
  onApply: (filters: Filters) => void;
  onClear: () => void;
  filters: Filters;
}

const ViewReport: React.FC<ViewReportProps> = ({ visible, onClose, onApply, onClear, filters }) => {
  const [localFilters, setLocalFilters] = useState<Filters>(filters);
  const [procurementType, setProcurementType] = useState<ProcurementTypeEnum | ''>('');

  const handleFieldChange = (field: keyof Filters, value: string | string[]) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (date: any, dateString: string) => {
    setLocalFilters(prev => ({
      ...prev,
      date: dateString,
    }));
  };

  const handleClear = () => {
    const emptyFilters: Filters = {
      procurementType: '',
      vendor: [],
      productType: '',
      brand: '',
      model: '',
      color: '',
      date: '',
    };
    console.log(localFilters);
    setProcurementType('');
    setLocalFilters(emptyFilters);
    console.log(localFilters);

    onClear();
  };


  return (
      <Modal title="View Report" open={visible} onCancel={onClose} footer={null} width={750} className="custom-modal">
          <div className="space-y-6">
              {/* Row 1: Procurement Type and Vendor */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-sm font-medium text-gray-700">Procurement Type</label>
                      <Form.Item>
                          <Select
                              placeholder="Select Procurement Type"
                              value={procurementType}
                              onChange={value => {
                                  setProcurementType(value); // update local state
                                  handleFieldChange('procurementType', value as string); // update form/parent filter
                              }}
                          >
                              {Object.entries(ProcurementTypeEnum).map(([key, val]) => (
                                  <Select.Option key={val} value={val}>
                                      {key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()}
                                  </Select.Option>
                              ))}
                          </Select>
                      </Form.Item>
                  </div>

                  <div>
                      <label className="block text-sm font-medium text-gray-700">Vendor</label>
                      <NewSearchableDropdown<IVendorEntity>
                          keyProp="id"
                          valueProp="vendor_name"
                          mode="multiple"
                          queryKey={['getVendors']}
                          defaultMultiValue={localFilters.vendor}
                          queryFnParams={{
                              apiRoute: 'vendor',
                              relations: [],
                              fields: ['id', 'vendor_name'],
                              filter: [],
                              predefinedFilters: [],
                          }}
                          onSelectionChange={value => handleFieldChange('vendor', value as string[])}
                      />
                  </div>
              </div>

              {/* Row 2: Product Type and Brand */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-sm font-medium text-gray-700">Product Type</label>
                      <NewSearchableDropdown<IProductTypeEntity>
                          keyProp="id"
                          valueProp="name"
                          queryKey={['getProductTypes']}
                          defaultMultiValue={localFilters.productType as unknown as []}
                          queryFnParams={{
                              apiRoute: 'product_type',
                              relations: [],
                              fields: ['id', 'name'],
                              filter: [],
                              predefinedFilters: [],
                          }}
                          onSelectionChange={value => handleFieldChange('productType', value as string)}
                      />
                  </div>

                  <div>
                      <label className="block text-sm font-medium text-gray-700">Brand</label>
                      <NewSearchableDropdown<IBrandEntity>
                          keyProp="id"
                          valueProp="name"
                          queryKey={['getBrands']}
                          defaultMultiValue={localFilters.brand as unknown as []}
                          queryFnParams={{
                              apiRoute: 'brand',
                              relations: [],
                              fields: ['id', 'name'],
                              filter: [],
                              predefinedFilters: [],
                          }}
                          onSelectionChange={value => handleFieldChange('brand', value as string)}
                      />
                  </div>
              </div>

              {/* Row 3: Model and Color */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-sm font-medium text-gray-700">Model</label>
                      <NewSearchableDropdown<IBrandModelEntity>
                          keyProp="id"
                          valueProp="name"
                          enabled={!!localFilters.brand}
                          defaultMultiValue={localFilters.model as unknown as []}
                          queryKey={['getModels', localFilters.brand]}
                          queryFnParams={{
                              apiRoute: 'brand_model',
                              relations: ['brand'],
                              fields: ['id', 'name'],
                              filter: [
                                  {
                                      field: 'brand.id',
                                      operator: FilterOperatorsEnum.EQUAL,
                                      value: localFilters.brand,
                                  },
                              ],
                              predefinedFilters: [],
                          }}
                          onSelectionChange={value => handleFieldChange('model', value as string)}
                      />
                  </div>

                  <div>
                      <label className="block text-sm font-medium text-gray-700">Color</label>
                      <NewSearchableDropdown<IModelColorsEntity>
                          keyProp="id"
                          valueProp="color_name"
                          queryKey={['getModelColors']}
                          defaultMultiValue={localFilters.color as unknown as []}
                          queryFnParams={{
                              apiRoute: 'model_color',
                              relations: [],
                              fields: ['id', 'color_name'],
                              filter: [],
                              predefinedFilters: [],
                          }}
                          onSelectionChange={value => handleFieldChange('color', value as string)}
                      />
                  </div>
              </div>

              {/* Row 4: Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-sm font-medium text-gray-700">Date</label>
                      <DatePicker value={localFilters.date ? dayjs(localFilters.date) : undefined} onChange={handleDateChange} className="w-full mt-1" />
                  </div>
              </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
              <div className="flex space-x-4">
                  <Button onClick={onClose} className="text-gray-500">
                      Cancel
                  </Button>
                  <Button onClick={handleClear}>Clear</Button>
                  <Button onClick={() => onApply(localFilters)} type="primary" className="bg-red-600 hover:bg-red-700 text-white">
                      View
                  </Button>
              </div>
          </div>
      </Modal>
  );
};

export default ViewReport;
